import { Component, EventEmitter, Input, Output, SimpleChanges, OnChanges, Injector } from '@angular/core';
import { HoldpointDialogComponent } from 'src/app/controls/holdpoint-dialog/holdpoint-dialog.component';
import { FormStatusEnum } from 'src/app/common/enumerations/enumerations';
import { DataButtonSignature } from 'src/app/common/models/data-button-signature';
import { YesNoDialogComponent } from 'src/app/controls/yes-no-dialog/yes-no-dialog.component';
import { WFAction, WfSignature, WfSignatureResource, WFTaskSignatureResource } from 'src/app/components/workflow/workflow';
import { SCFMaster, SCFHoldPointResource, SaveFormViewStateResource } from '../../../scf';
import { SCFPrivileges, ScfV2Service } from '../../../scf-v2.service';
import { EnvironmentService } from 'src/app/services/environment/environment.service';
import { TesterToolsService } from 'src/app/services/environment/tester-tools.service';
import { BaseComponent } from 'src/app/common/base/base.component';
import { utils } from 'src/app/modules/libs/utils';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray } from '@angular/cdk/drag-drop';
import { FormViewState } from 'src/app/common/models/form-view-state';
import { RSFHoldPoint } from 'src/app/components/rsf/rsf';

@Component({
  selector: 'scf-v2-main-s4-holdpoints',
  templateUrl: './scf-v2-main-s4-holdpoints.component.html',
  styleUrls: ['./scf-v2-main-s4-holdpoints.component.scss']
})
export class ScfV2MainS4HoldpointsComponent extends BaseComponent implements OnChanges {

  @Input() scfMaster?: SCFMaster | null;
  @Input() amendmentID?: number;
  @Input() isLogView?: boolean = false;
  @Input() disabled?: boolean;
  @Input() privileges?: SCFPrivileges;
  @Input() status?: FormViewState;

  @Output() changed = new EventEmitter<SCFMaster>();

  signatures?: WfSignature[];

  wfSignatures?: WfSignatureResource[];
  wfSignCount?: number;

  holdPoints?: SCFHoldPointResource[];
  enableReorder?: boolean;
  showNoHoldpointsFound = false;

  originalHoldpoints?: SCFHoldPointResource[];

  constructor(
    private scfService: ScfV2Service,
    private environmentService: EnvironmentService,
    private testerToolsService: TesterToolsService,
    protected override injector: Injector
  ) {
    super(injector);
  }

  ngOnChanges(changes: SimpleChanges): void {
    setTimeout(() => {
      this.getValues();
    }, 500);

  }

  amendmentActive() {
    return this.scfMaster?.scfAmendments?.find(a => a.active);
  }

  /** Indicates whether the current draft or active amendment requires sequential hold-point signatures. */
  get requireHoldPointSignOrder(): boolean {
    const amendment = this.amendmentActive();
    return this.status?.isViewingAmendment
      ? amendment?.requireHoldPointSignOrder ?? false
      : this.scfMaster?.requireHoldPointSignOrder ?? false;
  }

  /** Indicates whether the amendment changes the original hold-point signing policy. */
  get holdPointSignOrderChanged(): boolean {
    const amendmentValue = this.amendmentActive()?.requireHoldPointSignOrder ?? false;
    const originalValue = this.scfMaster?.requireHoldPointSignOrder ?? false;
    return amendmentValue !== originalValue;
  }

  /** Updates the selected signing policy on the draft or active amendment and notifies the parent form. */
  set requireHoldPointSignOrder(value: boolean) {
    const amendment = this.amendmentActive();
    if (this.status?.isViewingAmendment && amendment)
      amendment.requireHoldPointSignOrder = value;
    else if (this.scfMaster)
      this.scfMaster.requireHoldPointSignOrder = value;

    // Mark checkbox-only amendment changes so Update Amendment sends the save request.
    this.setFormDirty();

    if (this.scfMaster)
      this.changed.emit(this.scfMaster);
  }

  /** Determines whether the signing policy checkbox should be available for the current form state. */
  get canEditHoldPointSignOrder(): boolean {
    return !!(
      this.status?.isCreating ||
      this.status?.isEditing ||
      this.status?.isCreatingAmendment ||
      this.status?.isEditingAmendment
    );
  }

  getValues() {
    this.wfSignatures = [];
    this.signatures = [];
    this.enableReorder = false;
    if (this.scfMaster) {
      this.wfSignCount = this.wfSignatures?.filter(x => x.signed).length;

      if (!this.isLogView) {
        if (this.status?.isViewingAmendment) {
          this.amendmentID = this.scfMaster.scfAmendments?.find(a => a.active)?.id;
          this.holdPoints = this.utils.cloneDeep(this.scfMaster.scfHoldPoints?.filter(hp => hp.status == 1 && (hp.amendmentID ?? 0) == this.amendmentID).sort((a, b) => a.number - b.number));
        }
        else {
          this.amendmentID = 0;
          this.holdPoints = this.utils.cloneDeep(this.scfMaster.scfHoldPoints?.filter(hp => hp.status == 1 && !hp.amendmentID).sort((a, b) => a.number - b.number));
        }
        this.renumberHoldpoints();
      }
      else {
        if (this.amendmentID == 0)
          this.holdPoints = this.utils.cloneDeep(this.scfMaster.scfHoldPoints?.filter(hp => !hp.amendmentID && !hp.amendedID).sort((a, b) => a.number - b.number));
        else
          this.holdPoints = this.utils.cloneDeep(this.scfMaster.scfHoldPoints?.sort((a, b) => a.number - b.number));
      }



      const hasActiveHoldpoints = this.holdPoints?.some(hp => !hp.amendmentID || !(this.scfMaster?.scfAmendments?.find(a => a.id == hp.amendmentID && !a.active && a.status > 0)));

      const hasAmendmentHoldpoints = this.holdPoints?.some(hp => hp.amendmentID && (this.scfMaster?.scfAmendments?.find(a => a.id == hp.amendmentID && a.active)));

      this.showNoHoldpointsFound = !hasActiveHoldpoints && !hasAmendmentHoldpoints;

      if (!this.isLogView)
        this.enumerateAmendments();
      this.createSignatures();

      this.originalHoldpoints = this.scfMaster.scfHoldPoints?.filter(hp => !hp.amendmentID && hp.status == 1);
      if (this.originalHoldpoints?.length)
        this.originalHoldpoints.forEach(hp => hp.signatures = this.getSignatures(hp));
    }
  }

  processOriginalHP() {
    this.originalHoldpoints = this.scfMaster?.scfHoldPoints?.filter(hp => !hp.amendmentID && hp.status == 1);
    if (this.originalHoldpoints?.length)
      this.originalHoldpoints.forEach(hp => hp.signatures = this.getSignatures(hp));
  }

  enumerateAmendments() {
    const amendments = this.scfMaster?.scfAmendments?.filter(a => a.status != -1);
    let number = 1;
    if (amendments?.length) {
      amendments.map(a => {
        a.number = number;
        this.holdPoints?.filter(hp => hp.amendmentID == a.id).map(hp => hp.amendmentNumber = a.number);
        number++;
      });
    }
  }

  /**
   * Builds the hold-point signature cards and preserves role labels from either signature or task-local resources.
   */
  createSignatures() {
    if (!this.isLogView) {
      this.wfSignatures = this.scfMaster?.wfTable?.wfTableLocal?.wfSectionLocals?.find(x => x.number == 3)?.wfSignatures?.filter(x => x.wfTaskLocal.wfStepLocal?.code == 'HOLDPOINT' && x.holdPointID && x.required);

      if (this.status?.isViewingAmendment) {
        const amendment = this.scfMaster?.scfAmendments?.find(x => x.active);

        const requesterSignatures = this.wfSignatures?.filter(x => x.requesterSignature && !x.signed);
        const requesterAmended = this.scfMaster?.requesterId != amendment?.requesterId;
        if (requesterAmended && requesterSignatures && amendment) {
          const user = this.getUser(amendment.requesterId);
          requesterSignatures.forEach(rs => {
            rs.users = [];
            if (user)
              rs.users.push(user);
          });
        }
      }
      else {
        const requesterSignatures = this.wfSignatures?.filter(x => x.requesterSignature && !x.signed);
        const user = this.getUser(this.scfMaster?.requesterId);
        requesterSignatures?.forEach(rs => {
          rs.users = [];
          if (user)
            rs.users.push(user);
        });
      }

      if (this.wfSignatures) {
        for (const t of this.wfSignatures) {
          // Some SCF responses omit the signature role summary but retain roles on the task local.
          const signatureRoles = t.roles?.filter(r => !!r) ?? [];
          const taskRoles = t.wfTaskLocal?.wfRoleLocals ?? [];
          const signature: WfSignature = {
            id: t.id,
            approveActionId: t.approveID,
            unapproveActionId: t.unapproveID,
            disapproveActionId: t.disapproveID,
            amendmentId: t.amendmentID,
            code: t.code,
            name: t.titleBefore,
            roles: signatureRoles.length ? signatureRoles.map(r => r.id) : taskRoles.map(r => r.roleID),
            roleCodes: !t.requesterSignature
              ? (signatureRoles.length
                ? signatureRoles.sort((a, b) => utils.sort(a.code, b.code)).map(r => r.code)
                : taskRoles.map(r => r.role?.code ?? r.roleCode).filter(code => !!code).sort((a, b) => utils.sort(a, b))).join('/')
              : undefined,
            disabled: this.disabled,
            testSignReady: t.testSignReady,
            sectionNumber: 3,
            approvedBy: t.signedBy,
            approved: t.signed,
            approvedOn: t.signedOn,
            approveAction: t.approveEnabled && !this.userSigned(t),
            unapproveAction: t.unapproveEnabled,
            disapproveAction: t.disapproveEnabled,
            amendment: t.amendmentEnabled,
            required: t.required,
            taskId: t.taskID,
            value: t.value,
            warning: false,
            enabled: t.enabled,
            holdPointID: t.holdPointID,
            requesterSignature: t.requesterSignature,
            userNames: t.users?.map((r) => r?.name)[0] ?? (t.requesterSignature ? this.scfMaster?.requesterName : undefined),
          };
          this.wfSignCount = this.signCount();
          this.signatures?.push(signature);
        }
        this.checkStatus();
      }
    }
    else if (this.holdPoints?.length) {
      this.wfSignatures = [];
      const wfTaskLocals = this.holdPoints.flatMap(h => h.wfTaskLocals).filter(t => t?.wfTasks?.[0].enabled);
      wfTaskLocals.forEach(t => {
        const signature: WfSignature = {
          id: t?.id,
          disabled: true,
          amendmentId: t?.amendmentID,
          name: t?.name,
          roles: t?.wfRoleLocals?.map(rl => rl.roleID),
          roleCodes: !t?.requesterSignature ? t?.wfRoleLocals?.map(rl => rl.role?.code).sort((a, b) => utils.sort(a, b)).join('/') : undefined,
          approvedBy: this.getUserInfo(t?.wfTasks?.[0]?.signedBy?.id ?? 0),
          approved: !!t?.wfTasks?.[0].signedBy,
          approvedOn: t?.wfTasks?.[0].signedOn,
          approveAction: t?.wfActionLocals?.find(a => a.action == WFAction.Approve) && !t?.wfTasks?.[0].signedBy,
          unapproveAction: t?.wfActionLocals?.find(a => a.action == WFAction.Unapprove) && !!t?.wfTasks?.[0].signedBy,
          // amendment: t.amendmentEnabled,
          required: true,
          // taskId: t.taskID,
          // value: t.value,
          // warning: false,
          enabled: true,
          holdPointID: t?.scfHoldPointID,
          requesterSignature: t?.requesterSignature,
          userNames: t?.requesterSignature ? this.scfMaster?.requesterName : undefined
        };
        this.wfSignCount = this.signCount();
        this.signatures?.push(signature);
      });
    }
    if (this.signatures?.length) {
      this.holdPoints?.forEach(hp => {
        hp.signatures = this.getSignatures(hp);
      });
    }
  }

  userSigned(t: WfSignatureResource) {
    const alreadySigned = this.wfSignatures?.find(
      x => x.holdPointID == t.holdPointID &&
        x.signedBy?.id == this.getCurrentUser()?.id && x.id != t.id
    );
    return alreadySigned != null;
  }

  getSignatures(holdPoint: SCFHoldPointResource) {
    let number = 1;
    const signatures = this.signatures?.filter(x => x.holdPointID == holdPoint.id);
    signatures?.map(s => {
      s.name = `Hold Point ${holdPoint.number} Inspection ${number}`;
      s.number = number.toString();
      number++;
    });
    return signatures;
  }

  signCount() {
    let count = 0;
    if (this.wfSignatures) {
      this.wfSignatures.map(t => {
        if (t?.signed) { count++; }
      });
    }
    return count;
  }

  approve(s: WfSignature) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.approveActionId
    };

    this.scfService.sign(wfTaskSignature).toPromise()
      .catch(error => {
        console.log(error);
        this.alert.defaultError();
      });
  }

  /**
   * Whether the tester-only "auto-sign as role" tooling should be shown.
   * Gated defensively: only on non-production builds AND for System Administrators,
   * mirroring scf-v2-main-s1/s3/s4's showTestTools so it can never surface in production.
   */
  get showTestTools(): boolean {
    return !this.environmentService.isProduction() && this.isSA() && this.testerToolsService.enabled;
  }

  /**
   * Tester convenience: whether to offer the "Tester Approve" button for a hold-point inspection
   * signature. Mirrors scf-v2-main-s4's canTestSign. The backend sets `testSignReady` (a
   * role/user-independent readiness flag — for hold points it folds in the HOLDPOINT step-enabled
   * case), so the button appears on inspections whose turn it currently is, but only when the
   * logged-in SA cannot actually complete the approval themselves (no role, or has-role-but-greyed,
   * e.g. they already signed another inspection in the same hold point). The server-side TestSign
   * resolves the appropriate signer in either case.
   */
  canTestSign(s: WfSignature): boolean {
    return (
      this.showTestTools &&
      s.sectionNumber === 3 &&
      !!s.testSignReady &&
      !this.disabled &&
      !!s.approveActionId &&
      !(s.approveAction && !s.disabled)
    );
  }

  /**
   * Tester tooling (non-production): applies this hold-point inspection signature on behalf of the
   * appropriate user (the first holder of one of the signature's roles) via the non-prod, SA-gated
   * TestSign endpoint. The signer is resolved server-side, and the backend re-evaluates the
   * Active/Hold Point status after signing; the SignalR refresh that follows reloads the form.
   */
  async testSign(s: WfSignature) {
    if (!this.canTestSign(s)) return;
    // Confirmation popup intentionally disabled for now — the tooltip on the
    // "Tester Approve" button plus the non-prod/SA guards are sufficient. Kept
    // (commented) so it can be re-enabled easily if we want the prompt back.
    // const confirm = this.dialog.open(YesNoDialogComponent, {
    //   width: '450px',
    //   data: {
    //     message:
    //       'Tester tool (non-production): apply the "' +
    //       (s.name ?? 'this') +
    //       '" inspection as the appropriate user (' +
    //       (s.roleCodes || 'the required role') +
    //       ')? This signs the form as a different user.',
    //   },
    // });
    // const ok = await confirm.afterClosed().toPromise();
    // if (!ok) return;

    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.approveActionId,
    };
    this.scfService.testSign(wfTaskSignature).toPromise().then(
      (data) => {
        this.scfMaster = data;
        this.getValues();
      },
      (error) => {
        console.log(error);
        this.alert.defaultError();
      }
    );
  }

  async disapprove(s: WfSignature, data: DataButtonSignature) {
    // Get an array of promises
    const signPromises = this.signatures
      ?.filter(x => x.holdPointID == s.holdPointID && x.approved)
      .map(s => {
        const wfTaskSignature: WFTaskSignatureResource = {
          taskId: s.taskId,
          actionId: s.unapproveActionId,
          reason: data.text
        };
        return this.scfService.sign(wfTaskSignature).toPromise().then(() => {
          const signature = this.signatures?.find(x => x.id == s.id);
          if (signature) signature.approved = false;
        });
      });

    // Wait for all promises to resolve
    if (signPromises) {
      await Promise.all(signPromises);
    }

    this.checkStatus(data);
  }


  unapprove(s: WfSignature) {
    const wfTaskSignature: WFTaskSignatureResource = {
      taskId: s.taskId,
      actionId: s.unapproveActionId
    };
    this.scfService.sign(wfTaskSignature).toPromise().then(() => {
      const signature = this.signatures?.find(x => x.id == s.id);
      if (signature) signature.approved = false;
      this.checkStatus();
    }, error => {
      console.log(error);
      this.alert.defaultError();
    });
  }

  checkStatus(data?: any) {
    if (!this.scfMaster || !this.signatures) return;

    const relevantSignatures = this.signatures.filter(
      s => s.holdPointID && this.holdPoints?.some(h => h.id === s.holdPointID)
    );
    const hasUnapprovedSignatures = relevantSignatures.some(s => !s.approved);

    let status: FormStatusEnum | undefined;
    if (this.scfMaster.status === FormStatusEnum.Active && relevantSignatures.length && hasUnapprovedSignatures) {
      status = FormStatusEnum.HoldPoint;
    } else if (
      this.scfMaster.status === FormStatusEnum.HoldPoint &&
      (!relevantSignatures.length || !hasUnapprovedSignatures)
    ) {
      status = FormStatusEnum.Active;
    }

    if (status) {
      const scf: SaveFormViewStateResource = {
        id: this.scfMaster.id,
        status,
        disapprovalReason: status === FormStatusEnum.Active ? data?.text : undefined
      };

      this.scfService.putStatus(scf).toPromise()
        .catch(error => console.error(error));
    }
  }


  async update() {
    if (this.scfMaster)
      return await this.scfService.update(this.scfMaster).toPromise();
    else return null;
  }

  /**
   * Opens the shared hold-point dialog and creates the returned SCF hold point.
   */
  createHoldPoint() {
    const scfHoldPoint = {
      id: 0
    } as SCFHoldPointResource;
    const confirm = this.dialog.open(HoldpointDialogComponent, {
      width: '65vw',
      disableClose: true,
      data: {
        // The form discriminator lets the shared dialog apply SCF parent identifiers.
        formType: 'SCF',
        form: this.scfMaster,
        holdPoint: scfHoldPoint
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data && this.scfMaster) {
        if (this.isFormDirty()) await this.update();
        this.scfService.createHoldPoint(data).toPromise()
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }

  /**
   * Opens the shared hold-point dialog and saves changes to an SCF hold point.
   * @param scfHoldPoint Existing SCF hold point to edit.
   */
  editHoldPoint(scfHoldPoint: SCFHoldPointResource) {
    const confirm = this.dialog.open(HoldpointDialogComponent, {
      width: '65vw',
      disableClose: true,
      data: {
        // The form discriminator lets the shared dialog apply SCF parent identifiers.
        formType: 'SCF',
        form: this.scfMaster,
        holdPoint: scfHoldPoint
      }
    });
    confirm.afterClosed().toPromise().then(async (data: SCFHoldPointResource) => {
      if (data && this.scfMaster) {
        if (this.isFormDirty()) await this.update();
        this.scfService.updateHoldPoint(data).toPromise()
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }

  deleteHoldPoint(scfHoldPoint: SCFHoldPointResource) {
    const confirm = this.dialog.open(YesNoDialogComponent, {
      disableClose: true,
      data: {
        message: 'Are you sure you want to Delete this Hold Point?',
        icon: 'stop'
      }
    });
    confirm.afterClosed().toPromise().then(async data => {
      if (data && this.scfMaster) {
        if (this.isFormDirty()) await this.update();
        this.scfService.deleteHoldPoint(scfHoldPoint).toPromise()
          .catch(error => {
            console.log(error);
            this.alert.defaultError();
          });
      }
    });
  }

  holdPointUnsigned(hp: SCFHoldPointResource) {
    return hp.wfTaskLocals?.filter(x => x.wfTasks?.some(t => t.signedBy)).length == 0;
  }

  nextHoldPointUnsigned(hp: SCFHoldPointResource) {
    const nextHoldPoint = this.scfMaster?.scfHoldPoints?.find(x => x.number > hp.number);
    return nextHoldPoint ? nextHoldPoint.wfTaskLocals?.filter(x => x.wfTasks?.some(t => t.signedBy)).length == 0 : true;
  }

  holdPointFinished(hp: SCFHoldPointResource) {
    return this.signatures?.filter(x => x.holdPointID == hp.id && !x.approved).length == 0;
  }

  // This method will be called when an item is dropped
  // drop(event: CdkDragDrop<any[]>) {
  //   moveItemInArray(this.holdPoints ?? [], event.previousIndex, event.currentIndex);
  //   this.renumberHoldpoints();
  // }

  drop(event: CdkDragDrop<any>) {
    if (event.previousIndex === event.currentIndex) return;

    const draggedHp = event.item.data as SCFHoldPointResource;

    if (this.isHoldPointDragDisabled(draggedHp)) {
      return;
    }

    const targetHp = event.container.data[event.currentIndex];

    if (targetHp && this.isHoldPointDragDisabled(targetHp)) {
      return;
    }

    moveItemInArray(this.holdPoints ?? [], event.previousIndex, event.currentIndex);

    this.renumberHoldpoints(true);
  }

  /**
   * Renumbers hold points contiguously, optionally preserving positions locked during reordering.
   * Synchronizes the owning form collection so submission preserves the displayed order.
   * @param preserveLockedNumbers Whether signed or otherwise drag-disabled positions must remain fixed.
   */
  renumberHoldpoints(preserveLockedNumbers = false) {
    // Exit early when there are no hold points to normalize.
    if (!this.holdPoints?.length) return;

    // Load-time normalization repairs historical gaps; drag operations preserve locked positions.
    const disabledNumbers = new Set(
      this.holdPoints
        .filter(h => preserveLockedNumbers && this.isHoldPointDragDisabled(h))
        .map(h => h.number)
    );

    let nextNumber = 1;
    // Track only hold points whose number changed for selective backend update.
    const changed: SCFHoldPointResource[] = [];

    for (const hp of this.holdPoints) {
      // Keep drag-disabled hold points untouched.
      if (preserveLockedNumbers && this.isHoldPointDragDisabled(hp)) {
        continue;
      }

      // Skip any reserved number before assigning the next available value.
      while (disabledNumbers.has(nextNumber)) {
        nextNumber++;
      }

      // Apply and collect the update only when the number actually changes.
      if (hp.number !== nextNumber) {
        hp.number = nextNumber;
        // The displayed list is cloned; submission and refresh read the owning form collection.
        const source = this.scfMaster?.scfHoldPoints?.find(h => h.id == hp.id);
        if (source) source.number = nextNumber;
        changed.push(hp);
      }

      nextNumber++;
    }

    // Re-sort to keep visual and processing order aligned with current numbering.
    this.holdPoints = [...this.holdPoints].sort((a, b) => a.number - b.number);

    // Persist renumbered hold points unless the component is in read-only log view.
    if (changed.length && !this.isLogView) {
      this.scfService?.updateHoldPointNumbers(changed).toPromise();
    }
  }

  diffHoldpoint(hp: SCFHoldPointResource) {
    if (this.isLogView || !this.status?.isViewingAmendment) return false;
    const originalHP = this.originalHoldpoints?.find(ohp => hp.number == ohp.number);

    if (hp.description != originalHP?.description) return true;
    if (hp.signatures?.length != originalHP?.signatures?.length) return true;

    let diff = false;
    hp.signatures?.forEach(cs => {
      const os = originalHP.signatures?.find(s => s.number == cs.number);
      if (cs.roleCodes != os?.roleCodes) diff = true;
    });

    return diff;
  }

  /**
   * Restricts dragging to active reorder sessions while allowing signed hold points to be repositioned.
   * @param hp Hold point rendered by the drag list.
   * @returns True when reordering is inactive or the component is read-only.
   */
  isHoldPointDragDisabled(hp: SCFHoldPointResource): boolean {
    // Signing locks hold-point content, but amendments may still change its sequence.
    return !this.enableReorder || !!this.isLogView;
  }

  canSortHoldPoint = (
    index: number,
    drag: CdkDrag<SCFHoldPointResource>,
    drop: CdkDropList<SCFHoldPointResource[]>
  ): boolean => {
    const draggedHp = drag.data;
    const targetHp = drop.data[index];

    // Dragged item cannot move
    if (this.isHoldPointDragDisabled(draggedHp)) {
      return false;
    }

    // Target item cannot be displaced
    if (targetHp && this.isHoldPointDragDisabled(targetHp)) {
      return false;
    }

    return true;
  };
}
